import { createEffect, createResource, createSignal, Show } from "solid-js";
import Modal from "./Modal";
import Text from "./Text";
import SkinViewer3D from "./SkinViewer3D";
import { resolveSkin } from "../services/skullTexture";
import { useDebounced } from "../hooks/useDebounced";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./SkullOwnerView.module.css";

interface PickerState {
  initialName: string;
  resolve: (result: { name: string; uuid: string } | null) => void;
}

const [pickerState, setPickerState] = createSignal<PickerState | null>(null);

export function showSkullOwnerPicker(initialName?: string): Promise<{ name: string; uuid: string } | null> {
  return new Promise((resolve) => {
    setPickerState({ initialName: initialName ?? "", resolve });
  });
}

export function SkullOwnerPickerHost() {
  const { t } = useTranslation();
  const state = pickerState;

  const [name, setName] = createSignal("");
  createEffect(() => {
    const opened = state();
    if (opened) setName(opened.initialName);
  });
  const debouncedName = useDebounced(name, 300);
  const [skin] = createResource(
    () => (debouncedName().trim().length > 0 ? debouncedName().trim() : null),
    (playerName) => resolveSkin({ name: playerName }),
  );

  const close = (result: { name: string; uuid: string } | null) => {
    state()?.resolve(result);
    setPickerState(null);
  };

  return (
    <Show when={state()}>
      <Modal open={true} onClose={() => close(null)} maxWidth="26rem" title={<Text val="SELLBOT_SKULL_DIALOG_TITLE" />}>
        <div class={styles.body}>
          <input
            type="text"
            class="commandtext"
            style={{ width: "100%" }}
            value={name()}
            placeholder={t("SELLBOT_SKULL_DIALOG_NAME_PLACEHOLDER")}
            onInput={(e) => setName(e.currentTarget.value)}
            autofocus
          />
          <Show
            when={debouncedName().trim().length > 0}
            fallback={<div class={styles.empty}><p><Text val="SELLBOT_SKULL_DIALOG_HINT" /></p></div>}
          >
            <Show
              when={!skin.loading}
              fallback={<div class={styles.center}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}
            >
              <Show
                when={skin()}
                fallback={<div class={styles.empty}><i class="fa-solid fa-user-slash" /><p><Text val="SKULL_NO_OWNER" /></p></div>}
              >
                {(resolved) => (
                  <div class={styles.viewer}>
                    <SkinViewer3D skinUrl={resolved().skinUrl} slim={resolved().model === "slim"} size={160} />
                  </div>
                )}
              </Show>
            </Show>
          </Show>
          <div class="h" style={{ "justify-content": "flex-end", gap: "var(--spacing-sm)" }}>
            <button type="button" class="btn btn-secondary" onClick={() => close(null)}>
              <Text val="COMMON_CANCEL" />
            </button>
            <button
              type="button"
              class="btn btn-primary"
              disabled={!skin()?.uuid}
              onClick={() => close({ name: skin()!.name ?? name().trim(), uuid: skin()!.uuid! })}
            >
              <Text val="SELLBOT_SKULL_DIALOG_CONFIRM" />
            </button>
          </div>
        </div>
      </Modal>
    </Show>
  );
}
